import { useCallback, useEffect, useMemo, useRef } from "react";

interface UseModalNavigationOptions<T> {
  /** The ordered list to navigate. For the records grid this is the loaded,
   * filtered server records — the same list the grid renders. */
  items: T[];
  /** Id of the currently-open item, or null when nothing is open. */
  selectedId: string | null;
  getId: (item: T) => string;
  /** Url to warm into the browser cache when an item becomes adjacent. */
  getImageUrl: (item: T) => string | null;
  /** Called to open a different item (e.g. setSelectedRecord). */
  onNavigate: (item: T) => void;
  /** Infinite-query pagination, so navigating off the last loaded item can
   * pull the next page and continue rather than dead-ending. */
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
}

interface UseModalNavigationReturn {
  /** null when at the first item — callers hide the prev affordance. */
  onPrev: (() => void) | null;
  /** null when at the last item and no further pages — callers hide it. */
  onNext: (() => void) | null;
  currentIndex: number;
  totalCount: number;
  isFetchingNextPage: boolean;
}

/**
 * Sequential prev/next navigation for a modal opened over a list (the image
 * viewer over the photo grid). Computes the current position, clamps at the
 * ends, and — when the list is paginated — fetches the next page and auto-
 * advances onto the first new item so the user can keep going past the last
 * loaded record. Adjacent images are prefetched so the next/prev swap is
 * instant.
 *
 * Keyboard handling deliberately lives in the modal, not here, so it can be
 * suppressed while editing or in an expanded overlay — state only the modal
 * knows about — and so every call site gets it uniformly.
 *
 * Ported from otus-web's hook of the same name.
 */
export function useModalNavigation<T>({
  items,
  selectedId,
  getId,
  getImageUrl,
  onNavigate,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}: UseModalNavigationOptions<T>): UseModalNavigationReturn {
  // Set when the user navigates past the last loaded item and we kick off a
  // page fetch; the auto-advance effect consumes it once new items arrive.
  const pendingNextRef = useRef(false);
  const prevItemsLengthRef = useRef(items.length);

  const currentIndex = useMemo(() => {
    if (selectedId === null) return -1;
    return items.findIndex((item) => getId(item) === selectedId);
  }, [items, selectedId, getId]);

  const onPrev = useCallback(() => {
    // Navigating backward cancels any forward auto-advance still waiting on a
    // page fetch — otherwise pressing Next (at the end) then Prev would yank
    // the user forward onto the new page once it lands.
    pendingNextRef.current = false;
    if (currentIndex <= 0) return;
    onNavigate(items[currentIndex - 1]);
  }, [currentIndex, items, onNavigate]);

  const onNext = useCallback(() => {
    if (currentIndex < 0) return;
    const nextItem = items[currentIndex + 1];
    if (nextItem) {
      onNavigate(nextItem);
      return;
    }
    // At the last loaded item — pull the next page, then auto-advance below.
    if (hasNextPage) {
      pendingNextRef.current = true;
      fetchNextPage?.();
    }
  }, [currentIndex, items, onNavigate, hasNextPage, fetchNextPage]);

  const hasPrev = currentIndex > 0;
  const hasNext =
    currentIndex >= 0 &&
    (currentIndex < items.length - 1 || Boolean(hasNextPage));

  // Warm the browser cache for the neighbours so a swap renders instantly.
  //
  // The preload MUST request with the same CORS mode as the eventual consumer
  // (`crossOrigin="anonymous"`), for two reasons. First, the displayed image
  // and the share `fetch` are CORS requests; an anonymous preload is the only
  // kind whose cached response they can reuse — a no-CORS preload caches an
  // entry they cannot touch, so the warm is wasted. Second, and worse, a
  // no-CORS preload *poisons* the cache: it stores a response with no
  // `Access-Control-Allow-Origin`, and the browser then serves that same entry
  // to the crossOrigin `<img>` and the `fetch`, which fail the CORS check.
  // That was the cause of the CORS error on prev/next navigation — opening a
  // photo straight from the grid was fine because the grid already warmed the
  // cache with an anonymous request. See specs/technical/s3-image-fetching.spec.md.
  useEffect(() => {
    if (currentIndex < 0) return;
    for (const neighbour of [items[currentIndex - 1], items[currentIndex + 1]]) {
      if (!neighbour) continue;
      const url = getImageUrl(neighbour);
      if (url) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = url;
      }
    }
  }, [currentIndex, items, getImageUrl]);

  // Proactively fetch the next page as soon as the user reaches the last
  // loaded item, so forward navigation rarely has to wait on the network.
  useEffect(() => {
    if (
      currentIndex >= 0 &&
      currentIndex >= items.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage?.();
    }
  }, [currentIndex, items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // When a page requested by onNext lands, advance onto the first new item.
  useEffect(() => {
    if (pendingNextRef.current && items.length > prevItemsLengthRef.current) {
      const nextItem = items[prevItemsLengthRef.current];
      if (nextItem) onNavigate(nextItem);
      pendingNextRef.current = false;
    }
    prevItemsLengthRef.current = items.length;
  }, [items.length, items, onNavigate]);

  return {
    onPrev: hasPrev ? onPrev : null,
    onNext: hasNext ? onNext : null,
    currentIndex,
    totalCount: items.length,
    isFetchingNextPage: isFetchingNextPage ?? false,
  };
}
